import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Settings, SlidersHorizontal, RotateCcw, AlertTriangle, History, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Spinner } from './ChallengeShared';

export function ConfigTab() {
  const [config, setConfig] = useState(null);
  const [dateJ0, setDateJ0] = useState('');
  const [saving, setSaving] = useState(false);
  const [coefficients, setCoefficients] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [coeffForm, setCoeffForm] = useState({});
  const [coeffSaving, setCoeffSaving] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [trainSettings, setTrainSettings] = useState({ calorie_discount: 30, rest_day_calories: true });
  const [trainSaving, setTrainSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const [cfgRes, coeffRes, trainRes] = await Promise.all([
        apiClient.get('/challenge/config'),
        apiClient.get('/challenge/coefficients'),
        apiClient.get('/training/settings'),
      ]);
      setConfig(cfgRes.data);
      setDateJ0(cfgRes.data.date_j0);
      setTrainSettings(trainRes.data);
      const c = coeffRes.data;
      setDefaults(c.defaults);
      delete c.defaults;
      delete c.updated_at;
      setCoefficients(c);
      setCoeffForm({
        coeff_score_mg: c.coeff_score_mg,
        coeff_score_mm: c.coeff_score_mm,
        palier_s4: c.palier_coefficients?.[0] ?? 0.33,
        palier_s8: c.palier_coefficients?.[1] ?? 0.66,
        palier_s12: c.palier_coefficients?.[2] ?? 1.0,
        palier_s12b: c.palier_coefficients?.[3] ?? 1.0,
        caution_par_palier: c.caution_par_palier,
        seuil_garde_fou: c.seuil_garde_fou,
        penalite_transparence: c.penalite_transparence,
        route_poids_pct: c.route_poids_pct ?? 4,
        route_gras_pct: c.route_gras_pct ?? 4,
        route_muscle_pct: c.route_muscle_pct ?? 4,
      });
    } catch { toast.error('Erreur chargement config'); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put('/challenge/config', { date_j0: dateJ0 });
      setConfig(data);
      toast.success('Dates mises a jour');
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const toggleActif = async () => {
    try {
      const { data } = await apiClient.put('/challenge/config', { actif: !config.actif });
      setConfig(data);
      toast.success(data.actif ? 'Challenge active !' : 'Challenge desactive');
    } catch { toast.error('Erreur'); }
  };

  const toggleVisibilite = async () => {
    try {
      const { data } = await apiClient.put('/challenge/config', { visible_membres: !(config.visible_membres !== false) });
      setConfig(data);
      toast.success(data.visible_membres !== false ? 'Challenge visible pour les membres' : 'Challenge masqué aux membres');
    } catch { toast.error('Erreur'); }
  };

  const saveCoefficients = async () => {
    setCoeffSaving(true);
    try {
      const payload = {
        coeff_score_mg: parseFloat(coeffForm.coeff_score_mg),
        coeff_score_mm: parseFloat(coeffForm.coeff_score_mm),
        palier_coefficients: [
          parseFloat(coeffForm.palier_s4),
          parseFloat(coeffForm.palier_s8),
          parseFloat(coeffForm.palier_s12),
          parseFloat(coeffForm.palier_s12b),
        ],
        caution_par_palier: parseInt(coeffForm.caution_par_palier),
        seuil_garde_fou: parseFloat(coeffForm.seuil_garde_fou),
        penalite_transparence: parseFloat(coeffForm.penalite_transparence),
        route_poids_pct: parseFloat(coeffForm.route_poids_pct),
        route_gras_pct: parseFloat(coeffForm.route_gras_pct),
        route_muscle_pct: parseFloat(coeffForm.route_muscle_pct),
      };
      const { data } = await apiClient.put('/challenge/coefficients', payload);
      setWarnings(data.warnings || []);
      setCoefficients(data.coefficients);
      toast.success('Coefficients sauvegardes');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setCoeffSaving(false); }
  };

  const resetCoefficients = async () => {
    if (!window.confirm('Remettre tous les coefficients aux valeurs par defaut ?')) return;
    try {
      const { data } = await apiClient.post('/challenge/coefficients/reset');
      setCoefficients(data.coefficients);
      setDefaults(data.defaults);
      setWarnings([]);
      fetchConfig();
      toast.success('Coefficients reinitialises');
    } catch { toast.error('Erreur reset'); }
  };

  const fetchLog = async () => {
    try {
      const { data } = await apiClient.get('/challenge/coefficients/log');
      setLogs(data);
      setShowLog(!showLog);
    } catch { toast.error('Erreur chargement historique'); }
  };

  const saveTrainSettings = async () => {
    setTrainSaving(true);
    try {
      const { data } = await apiClient.put('/training/settings', trainSettings);
      setTrainSettings(prev => ({ ...prev, ...data }));
      toast.success('Parametres entrainement sauvegardes');
    } catch { toast.error('Erreur'); }
    finally { setTrainSaving(false); }
  };

  const isModified = (key, val) => {
    if (!defaults) return false;
    const def = defaults[key];
    return def !== undefined && parseFloat(val) !== def;
  };

  const warnLevel = (key, val) => {
    if (!defaults) return null;
    const def = defaults[key];
    if (!def || def === 0) return null;
    const ratio = parseFloat(val) / def;
    if (ratio > 3 || ratio < 1/3) return 'danger';
    if (ratio > 2 || ratio < 0.5) return 'warn';
    return null;
  };

  if (!config || !coefficients) return <Spinner />;

  const dates = [
    { label: 'J0', key: 'date_j0' }, { label: 'S2', key: 'date_s2' }, { label: 'S4', key: 'date_s4' },
    { label: 'S6', key: 'date_s6' }, { label: 'S8', key: 'date_s8' }, { label: 'S10', key: 'date_s10' },
    { label: 'S12', key: 'date_s12' }
  ];

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Activation */}
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-5" data-testid="challenge-activation">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              <Settings className="w-4 h-4 text-[#FF6B35] inline mr-1" /> Activation
            </h3>
            <p className="text-xs text-[#AAAAAA] mt-1">{config.nom || 'Challenge Transformation'}</p>
          </div>
          <button onClick={toggleActif} data-testid="toggle-challenge-actif"
            className={`px-4 py-2 rounded-md text-sm font-bold transition-colors ${
              config.actif ? 'bg-[#2e7d32] text-white' : 'bg-[#888888]/20 text-[#888888]'
            }`}>
            {config.actif ? 'ACTIF' : 'INACTIF'}
          </button>
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#2A2A2E]">
          <div>
            <p className="text-sm font-bold text-white">Visibilité membres</p>
            <p className="text-xs text-[#AAAAAA] mt-0.5">Masquer l'onglet Challenge aux utilisateurs (les admins le voient toujours)</p>
          </div>
          <button onClick={toggleVisibilite} data-testid="toggle-challenge-visibilite"
            className={`px-4 py-2 rounded-md text-sm font-bold transition-colors flex-shrink-0 ${
              config.visible_membres !== false ? 'bg-[#2e7d32] text-white' : 'bg-[#c0392b] text-white'
            }`}>
            {config.visible_membres !== false ? 'VISIBLE' : 'MASQUÉ'}
          </button>
        </div>
      </div>

      {/* Dates */}
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-5 space-y-3">
        <h3 className="text-base font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Date de depart</h3>
        <Input type="date" value={dateJ0} onChange={e => setDateJ0(e.target.value)} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="config-date-j0" />
        <Button onClick={save} disabled={saving} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF8C55]">
          {saving ? 'Enregistrement...' : 'Recalculer les dates'}
        </Button>
      </div>


      {/* Training Settings */}
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-5 space-y-4" data-testid="training-settings">
        <h3 className="text-base font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          Parametres Entrainement
        </h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Abattement calories seance (%)</label>
            <div className="flex items-center gap-3">
              <input type="range" min="0" max="80" value={trainSettings.calorie_discount}
                onChange={e => setTrainSettings(prev => ({ ...prev, calorie_discount: parseInt(e.target.value) }))}
                className="flex-1 accent-[#FF6B35]" data-testid="calorie-discount-slider" />
              <span className="font-mono text-lg font-bold text-[#FF6B35] w-14 text-right" data-testid="calorie-discount-value">{trainSettings.calorie_discount}%</span>
            </div>
            <p className="text-[9px] text-zinc-600 mt-1">Reduit les calories estimees par seance pour tout le monde. Ex: 30% = une seance estimee a 300 kcal affichera ~210 kcal.</p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-400">Calories jours de repos (semaine)</p>
              <p className="text-[9px] text-zinc-600">Activite quotidienne hors entrainement</p>
            </div>
            <button onClick={() => setTrainSettings(prev => ({ ...prev, rest_day_calories: !prev.rest_day_calories }))}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${trainSettings.rest_day_calories ? 'bg-[#FF6B35] text-white' : 'bg-zinc-800 text-zinc-500'}`}
              data-testid="rest-day-toggle">
              {trainSettings.rest_day_calories ? 'ACTIF' : 'INACTIF'}
            </button>
          </div>
        </div>
        <Button onClick={saveTrainSettings} disabled={trainSaving} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF8C55]" data-testid="save-training-settings">
          {trainSaving ? 'Enregistrement...' : 'Sauvegarder'}
        </Button>
      </div>


      {/* Timeline */}
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4">
        <h3 className="text-sm font-bold text-white mb-3">Timeline</h3>
        <div className="space-y-2">
          {dates.map(d => (
            <div key={d.key} className="flex items-center justify-between">
              <span className={`text-xs font-bold ${d.key === 'date_j0' ? 'text-[#FF6B35]' : 'text-[#AAAAAA]'}`}>{d.label}</span>
              <span className="font-mono text-xs text-white">{config[d.key]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ COEFFICIENTS DE SCORING ═══ */}
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-5 space-y-4" data-testid="coefficients-section">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            <SlidersHorizontal className="w-4 h-4 text-[#FF6B35] inline mr-1" /> Coefficients de scoring
          </h3>
          <div className="flex gap-2">
            <button onClick={fetchLog} className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1" data-testid="coeff-history-btn">
              <History className="w-3 h-3" /> Historique
            </button>
            <button onClick={resetCoefficients} className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1" data-testid="coeff-reset-btn">
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          </div>
        </div>

        {warnings.length > 0 && (
          <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-md p-3">
            {warnings.map((w, i) => (
              <p key={i} className="text-xs text-[#FFB800] flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {w}
              </p>
            ))}
          </div>
        )}

        {/* Score Prix 1 */}
        <div>
          <p className="text-xs text-[#FF6B35] font-bold uppercase tracking-wider mb-2">Ponderation Score Prix 1</p>
          <p className="text-[10px] text-zinc-500 mb-2">Score = MG x coeff + MM x coeff (total = 1.0)</p>
          <div className="grid grid-cols-2 gap-3">
            <CoeffField label="Coeff MG" value={coeffForm.coeff_score_mg} onChange={v => setCoeffForm({...coeffForm, coeff_score_mg: v})} step={0.05} min={0} max={1} modified={isModified('coeff_score_mg', coeffForm.coeff_score_mg)} warn={warnLevel('coeff_score_mg', coeffForm.coeff_score_mg)} testId="coeff-score-mg" />
            <CoeffField label="Coeff MM" value={coeffForm.coeff_score_mm} onChange={v => setCoeffForm({...coeffForm, coeff_score_mm: v})} step={0.05} min={0} max={1} modified={isModified('coeff_score_mm', coeffForm.coeff_score_mm)} warn={warnLevel('coeff_score_mm', coeffForm.coeff_score_mm)} testId="coeff-score-mm" />
          </div>
        </div>

        {/* Paliers */}
        <div>
          <p className="text-xs text-[#FF6B35] font-bold uppercase tracking-wider mb-2">Coefficients paliers</p>
          <p className="text-[10px] text-zinc-500 mb-2">Progression vers l'objectif: S4=33%, S8=66%, S12=100% (P1 inscription = manuel)</p>
          <div className="grid grid-cols-4 gap-2">
            <CoeffField label="S4" value={coeffForm.palier_s4} onChange={v => setCoeffForm({...coeffForm, palier_s4: v})} step={0.01} min={0} max={2} testId="coeff-palier-s4" />
            <CoeffField label="S8" value={coeffForm.palier_s8} onChange={v => setCoeffForm({...coeffForm, palier_s8: v})} step={0.01} min={0} max={2} testId="coeff-palier-s8" />
            <CoeffField label="S12" value={coeffForm.palier_s12} onChange={v => setCoeffForm({...coeffForm, palier_s12: v})} step={0.01} min={0} max={2} testId="coeff-palier-s12" />
            <CoeffField label="Réserve" value={coeffForm.palier_s12b} onChange={v => setCoeffForm({...coeffForm, palier_s12b: v})} step={0.01} min={0} max={2} testId="coeff-palier-s12b" />
          </div>
        </div>

        {/* Caution */}
        <div>
          <p className="text-xs text-[#FF6B35] font-bold uppercase tracking-wider mb-2">Caution</p>
          <CoeffField label="Montant / palier (XPF)" value={coeffForm.caution_par_palier} onChange={v => setCoeffForm({...coeffForm, caution_par_palier: v})} step={500} min={0} max={50000} modified={isModified('caution_par_palier', coeffForm.caution_par_palier)} warn={warnLevel('caution_par_palier', coeffForm.caution_par_palier)} testId="coeff-caution" />
          <p className="text-[10px] text-zinc-500 mt-1">Total: {(parseInt(coeffForm.caution_par_palier) || 0) * 4} XPF (4 paliers)</p>
        </div>

        {/* Garde-fous */}
        <div>
          <p className="text-xs text-[#FF6B35] font-bold uppercase tracking-wider mb-2">Garde-fous</p>
          <div className="grid grid-cols-2 gap-3">
            <CoeffField label="Seuil disqualif." value={coeffForm.seuil_garde_fou} onChange={v => setCoeffForm({...coeffForm, seuil_garde_fou: v})} step={1} min={-20} max={0} testId="coeff-seuil" />
            <CoeffField label="Penalite transp." value={coeffForm.penalite_transparence} onChange={v => setCoeffForm({...coeffForm, penalite_transparence: v})} step={1} min={-20} max={0} testId="coeff-penalite" />
          </div>
        </div>

        {/* Objectifs uniques (regle pour tous) */}
        <div>
          <p className="text-xs text-[#FF6B35] font-bold uppercase tracking-wider mb-2">Objectifs (regle unique pour tous)</p>
          <p className="text-[10px] text-zinc-500 mb-2">Caution sauvee si UNE des 3 routes est atteinte (au prorata du palier) : poids -X% OU gras -Y% OU muscle +Z%</p>
          <div className="grid grid-cols-3 gap-2">
            <CoeffField label="Perte de poids (%)" value={coeffForm.route_poids_pct} onChange={v => setCoeffForm({...coeffForm, route_poids_pct: v})} step={0.5} min={1} max={20} modified={isModified('route_poids_pct', coeffForm.route_poids_pct)} warn={warnLevel('route_poids_pct', coeffForm.route_poids_pct)} testId="coeff-route-poids" />
            <CoeffField label="Perte de gras (%)" value={coeffForm.route_gras_pct} onChange={v => setCoeffForm({...coeffForm, route_gras_pct: v})} step={0.5} min={1} max={20} modified={isModified('route_gras_pct', coeffForm.route_gras_pct)} warn={warnLevel('route_gras_pct', coeffForm.route_gras_pct)} testId="coeff-route-gras" />
            <CoeffField label="Prise de muscle (%)" value={coeffForm.route_muscle_pct} onChange={v => setCoeffForm({...coeffForm, route_muscle_pct: v})} step={0.5} min={1} max={20} modified={isModified('route_muscle_pct', coeffForm.route_muscle_pct)} warn={warnLevel('route_muscle_pct', coeffForm.route_muscle_pct)} testId="coeff-route-muscle" />
          </div>
        </div>

        <Button onClick={saveCoefficients} disabled={coeffSaving} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF8C55]" data-testid="save-coefficients-btn">
          {coeffSaving ? 'Enregistrement...' : 'Sauvegarder les coefficients'}
        </Button>
      </div>

      {/* Log */}
      {showLog && (
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4" data-testid="coefficients-log">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-1">
              <History className="w-4 h-4 text-[#FF6B35]" /> Historique des modifications
            </h3>
            <button onClick={() => setShowLog(false)} className="text-zinc-400 hover:text-white">
              <ChevronUp className="w-4 h-4" />
            </button>
          </div>
          {logs.length === 0 ? (
            <p className="text-xs text-zinc-500">Aucune modification</p>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {logs.map((log, i) => (
                <LogEntry key={log.id || i} log={log} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CoeffField({ label, value, onChange, step = 0.1, min = 0, max = 100, modified, warn, testId }) {
  return (
    <div className="space-y-1">
      <Label className={`text-[10px] ${modified ? 'text-[#FFB800]' : 'text-zinc-400'}`}>
        {label} {modified && <span className="text-[#FFB800]">*</span>}
      </Label>
      <Input
        type="number"
        step={step}
        min={min}
        max={max}
        value={value}
        onChange={e => onChange(e.target.value)}
        className={`bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono h-8 text-xs ${
          warn === 'danger' ? 'border-red-500/60 bg-red-500/5' :
          warn === 'warn' ? 'border-[#FFB800]/40 bg-[#FFB800]/5' : ''
        }`}
        data-testid={testId}
      />
    </div>
  );
}

function LogEntry({ log }) {
  const [open, setOpen] = useState(false);
  const date = log.changed_at ? new Date(log.changed_at).toLocaleString('fr-FR') : '?';
  const isReset = log.action === 'reset_to_defaults';

  return (
    <div className="border border-[#2A2A2E] rounded-md overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/5">
        <div className="flex items-center gap-2">
          {isReset ? (
            <RotateCcw className="w-3 h-3 text-red-400" />
          ) : (
            <SlidersHorizontal className="w-3 h-3 text-[#FF6B35]" />
          )}
          <span className="text-xs text-white font-medium">{log.changed_by}</span>
          <span className="text-[10px] text-zinc-500">{date}</span>
        </div>
        {open ? <ChevronUp className="w-3 h-3 text-zinc-400" /> : <ChevronDown className="w-3 h-3 text-zinc-400" />}
      </button>
      {open && (
        <div className="px-3 py-2 bg-[#0F0F0F] border-t border-[#2A2A2E]">
          {isReset ? (
            <p className="text-[10px] text-red-400">Reset aux valeurs par defaut</p>
          ) : (
            <div className="space-y-1">
              {Object.entries(log.new_values || {}).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-[10px]">
                  <span className="text-zinc-400 font-mono">{k}:</span>
                  <span className="text-red-400 line-through">{JSON.stringify(log.previous?.[k])}</span>
                  <span className="text-zinc-500">→</span>
                  <span className="text-[#2e7d32]">{JSON.stringify(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
